import React, {
  createContext,
  useContext,
  useState,
  type ReactNode,
} from "react";

import Modal from "../components/Modal";

// ─────────────────────────────
// Tipovi (možeš ih dodatno skratiti ako želiš)
// ─────────────────────────────

type ModalType = "confirmation" | "alert";

interface ActiveModal {
  type: ModalType;
  data: any;
}

interface ModalContextValue {
  openModal: (type: ModalType, data: any) => void;
  closeModal: () => void;
  isOpen: boolean;
}

// ─────────────────────────────
// Context
// ─────────────────────────────

const ModalContext = createContext<ModalContextValue | null>(null);

// ─────────────────────────────
// Provider
// ─────────────────────────────

export function ModalProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeModal, setActiveModal] = useState<ActiveModal | null>(null);

  const openModal = (type: ModalType, data: any) => {
    setActiveModal({ type, data });
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
  };

  return (
    <ModalContext.Provider value={{ openModal, closeModal, isOpen }}>
      {children}

      {activeModal && (
        <>
          {activeModal.type === "confirmation" && (
            <Modal
              isOpen={isOpen}
              onClose={closeModal}
              title={activeModal.data.title}
              inputPlaceholder={activeModal.data.inputPlaceholder}
              initialValue={activeModal?.data?.initialValue}
              description={activeModal.data.description}
              content={activeModal.data.content}
              confirmLabel={activeModal.data.confirmLabel}
              cancelLabel={activeModal.data.cancelLabel}
              onConfirm={activeModal.data.onConfirm}
              onCancel={activeModal.data.onCancel}
            />
          )}

          {activeModal.type === "alert" && (
            <Modal
              isOpen={isOpen}
              onClose={closeModal}
              content={activeModal.data.content}
              initialValue={activeModal?.data?.initialValue}
              title={activeModal.data.title}
              inputPlaceholder={activeModal.data.inputPlaceholder}
              description={activeModal.data.description}
              confirmLabel={activeModal.data.confirmLabel || "OK"}
              cancelLabel=""
              onConfirm={() => {
                activeModal.data.onConfirm?.();
                closeModal();
              }}
            />
          )}
        </>
      )}
    </ModalContext.Provider>
  );
}

// ─────────────────────────────
// Hook
// ─────────────────────────────

export function useModal() {
  const ctx = useContext(ModalContext);

  if (!ctx) {
    throw new Error("useModal mora biti unutar ModalProvider");
  }

  return ctx;
}
